import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment/moment';
import { v1 as uuidv1 } from 'uuid';

import { log } from '../../../services/logging';
import type { Scene } from '../../../types/scene';
import { getUserId, selectUserCanSaveScene } from '../../connections/connectionsDuck';
import { isSharedStorageAvailable, sharedStorageApi } from '../../shared-storage/shared-storage.api';
import type { RootState } from '../../types';
import { getLatestSceneVersion } from '../migrations';
import { getScenes } from '../scene.selectors';
import { NAME } from './const';

export const buildScene = (
  userId: Scene['createdBy'],
  version: Scene['version'],
  scenesLength?: number,
  canSaveScene = true,
): Scene => {
  let name = '';
  if (canSaveScene) {
    name = `Untitled Scene${scenesLength && scenesLength > 0 ? ` ${scenesLength + 1}` : ''}`;
  } else {
    name = moment().format();
  }

  return {
    name,
    createdAt: new Date(), // todo change to optional
    id: uuidv1(),
    createdBy: userId,
    numOfNodes: 0, // for testing, you need to change value to 1, will be fixed when BE done
    numOfRels: 0, // for testing, you need to change value to 1, will be fixed when BE done
    roles: [],
    ranges: [],
    version,
    temporaryData: canSaveScene ? undefined : {},
  };
};

type CreateScenePayload = {
  perspectiveId: Scene['perspectiveId'];
};
type CreateSceneResult = {
  scene: Scene | null;
  perspectiveId?: Scene['perspectiveId'];
};
export const createSceneThunk = createAsyncThunk<CreateSceneResult, CreateScenePayload, { state: RootState }>(
  `${NAME}/createSceneThunk`,
  async ({ perspectiveId }: CreateScenePayload, { dispatch, getState }) => {
    const state = getState();
    const userId = getUserId(state);
    const sceneVersion = getLatestSceneVersion();
    const scenes = getScenes(state);
    const canSaveScene = selectUserCanSaveScene(state);

    if (!userId || !sceneVersion) {
      log.error(`Missing parameters to load scenes - userId: ${userId}, sceneVersion: ${sceneVersion}`);
      // throw new Error('User ID not present in state');
      // throw new Error('Version not available');
      return { scene: null };
    }

    let newScene = {
      ...buildScene(userId, sceneVersion, scenes.length, canSaveScene),
      perspectiveId,
    };
    if (isSharedStorageAvailable()) {
      const result = await dispatch(sharedStorageApi.endpoints.createScene.initiate(newScene));
      const { id, createdAt, lastModified } = result.data ?? {};
      if (typeof id !== 'string') {
        log.error('Could not get scene id from Shared Storage');
        return { scene: null };
      }
      newScene = { ...newScene, id, createdAt: createdAt ?? newScene.createdAt, lastModified };
    } else {
      newScene = {
        ...newScene,
        id: uuidv1(),
        createdAt: new Date(),
        lastModified: new Date(),
      };
    }

    return {
      scene: newScene,
      perspectiveId,
    };
  },
);
